import { useEffect, useState } from 'react';
import { MdOutlineClose } from 'react-icons/md';

interface HelpSupportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const HelpSupportModal: React.FC<HelpSupportModalProps> = ({ isOpen, onClose }) => {
    const [isClosing, setIsClosing] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
            setIsAnimating(false);
        }, 300);
    };

    // Trigger slide-in animation when modal opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => setIsAnimating(true), 10);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            <div
                className={`absolute inset-y-0 left-0 z-500 w-full max-w-md overflow-hidden bg-background shadow-2xl transition-transform duration-400 ease-out ${
                    isClosing ? '-translate-x-full' : isAnimating ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="relative flex h-full flex-col">
                    {/* Background */}
                    <div className="pointer-events-none absolute inset-0 z-0">
                        <img
                            src="/assets/background-skeleton.svg"
                            alt="Perspective grid background"
                            className="absolute bottom-0 left-1/2 w-[200%] -translate-x-1/2 select-none opacity-30"
                        />
                    </div>

                    {/* Header */}
                    <div className="h-[60px] relative z-10 flex items-center justify-between border-b border-[#e1e1e1] px-6 py-5">
                        <img src="/assets/logo-smaill.svg" alt="Podus AI" className="h-auto w-[30px]" />
                        <button
                            onClick={handleClose}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 transition-colors hover:bg-neutral-200"
                            aria-label="Close modal"
                        >
                            <MdOutlineClose size={24} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 flex-1 flex flex-col overflow-y-auto px-6 py-8">
                        <h2 className="mb-2 text-2xl font-bold text-[#151515]">Help & Support</h2>
                        <p className="mb-8 text-sm text-neutral-600">
                            Contact us via telephone and email for help and support
                        </p>

                        {/* Contact Information */}
                        <div className="space-y-6">
                            {/* Telephone */}
                            <div>
                                <h3 className="text-xs font-semibold text-[#151515] mb-2">Gavel Telephone</h3>
                                <a
                                    href="tel:09031167894"
                                    className="flex items-center gap-3 text-[#151515] hover:text-gray-700 transition-colors"
                                >
                                    <svg
                                        className="w-5 h-5 text-[#151515]"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                        />
                                    </svg>
                                    <span className="text-sm">0903 116 7894</span>
                                </a>
                            </div>

                            {/* Email */}
                            <div>
                                <h3 className="text-xs font-semibold text-[#151515] mb-2">Gavel Email</h3>
                                <a
                                    href="mailto:hello@gavel.ng"
                                    className="flex items-center gap-3 text-[#151515] hover:text-gray-700 transition-colors"
                                >
                                    <svg
                                        className="w-5 h-5 text-[#151515]"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                    </svg>
                                    <span className="text-sm">hello@gavel.ng</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HelpSupportModal;
